import { css } from '@compiled/react';
// @ts-expect-error ecmascript
import { useInView } from 'react-intersection-observer';
import { FormattedMessage, useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { Editions, ProductKeys } from '@atlassiansox/asc-stargate';

import { Adg4Button } from '@/components/adg4/button/src/button';
import { Adg4FramedImage } from '@/components/adg4/framed-image/src/framed-image';
import { useCrossFlowFrontendTouchpoint } from '@/components/cross-flow/touchpoint/use-touchpoint/index';
import TryIntent from '@/components/try-intent/src/try-intent';
import { Link } from '@/libraries/design/components/link/src/link';
import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { Scroller, ScrollerArray } from '@/patterns/adg4/scroller/src/scroller';
import { useScrollTracking } from '@/utils/scroll-tracking/scroll-tracking';

type ScrollerItem = {
	id: string;
	title: string;
	description: string;
	feature: {
		src: string;
		alt: string;
	};
	accent?: {
		src: string;
		alt: string;
	};
	bgShape: string;
	bgColor: string;
	lazyLoadImage: boolean;
	link?: {
		type: 'link' | 'button';
		text: string;
		label: string;
		labelEnglish: string;
		href: string;
		onClick?: (event: React.MouseEvent) => void;
		ariaLabelText?: string;
	};
};

type ArrowProps = {
	color?: string;
	rotate?: number;
	top?: number | string;
	right?: number | string;
	bottom?: number | string;
	left?: number | string;
	animate?: boolean;
};

const contentStyles = css({
	[media.below.sm]: {
		textAlign: 'center',
	},
});

const headingStyles = css({
	margin: 0,
});

const textStyles = css({
	margin: 0,
	paddingBlockStart: 16,
	paddingBlockEnd: 24,
});

const scrollerHeading = css({
	textAlign: 'center',
	width: '100%',
});

const buttonContainer = css({
	position: 'relative',
	display: 'inline-block',
});

const arrowStyles = css({
	position: 'absolute',
	animationDuration: '1s',
	animationDelay: '0.1s',
	animationIterationCount: 1,
	animationFillMode: 'backwards',
});

const sectionStyle = css({
	overflow: 'clip',
});

const arrowInAnimation = css({ animationName: 'arrowInBtnCta' });

export const ArrowPencilPointRight = (props: ArrowProps) => {
	const transformStyles = css({
		'@keyframes arrowInBtnCta': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'0%': {
				transform: `rotate(${props.rotate ?? 0}deg) translate(-150%, -75%)`,
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'100%': {
				transform: `rotate(${props.rotate ?? 0}deg) translate(0, 0)`,
			},
		},
	});

	return (
		<svg
			css={[arrowStyles, transformStyles, props.animate && arrowInAnimation]}
			style={{
				insetInlineStart: props.left,
				insetInlineEnd: props.right,
				insetBlockStart: props.top,
				insetBlockEnd: props.bottom,
				transform: `rotate(${props.rotate ? props.rotate : '0'}deg)`,
				color: props.color ? props.color : 'currentColor',
				visibility: props.animate ? 'visible' : 'hidden',
			}}
			width="72"
			height="25"
			viewBox="0 0 72 25"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M59.9377 15.3992C59.0173 14.9197 58.1224 14.8026 57.2375 14.6506C56.2764 14.4847 55.3298 14.5122 54.3661 14.6939C53.5179 14.8575 52.6572 14.756 51.8548 14.5047C50.3545 14.0308 48.8161 13.9548 47.2581 13.9487C46.2852 13.9435 45.2992 13.8949 44.3421 13.7474C43.6788 13.6425 43.0503 13.3313 42.3766 13.1053C41.985 13.7302 41.3847 13.7038 40.7654 13.4715C39.4868 12.9907 38.1709 12.9018 36.8181 12.9291C36.0821 12.9425 35.3546 12.7709 34.7504 12.2942C34.6729 12.2382 34.5745 12.21 34.4827 12.1765C33.8726 12.7787 33.1602 12.7406 32.4202 12.5737C31.2308 12.3121 30.0329 12.0735 28.8435 11.8119C28.0392 11.6324 27.2369 11.435 26.434 11.2435C25.6375 11.0468 24.8746 10.7635 24.004 10.9669C22.9249 11.2205 21.8431 10.958 20.7692 10.7864C19.5431 10.5869 18.3301 10.2689 17.1034 10.1833C15.0061 10.0312 12.9448 9.66091 10.8685 9.37304C9.87464 9.2337 8.87682 9.12994 7.89344 8.94972C6.89235 8.76766 5.89651 8.53817 4.91509 8.28619C3.86938 8.02152 2.82958 7.7035 1.79371 7.40388C1.12194 7.21408 0.740116 6.67063 0.443572 6.11206C0.359595 5.95341 0.591152 5.53371 0.780077 5.33746C1.3744 4.71561 2.13013 4.41639 2.99148 4.35003C4.92541 4.19732 6.85018 4.28953 8.7651 4.47067C10.4852 4.63154 12.1902 4.92878 13.8886 5.23134C15.8515 5.57338 17.8274 5.79684 19.8165 5.95571C21.368 6.07507 22.9135 6.30175 24.4618 6.45076C25.9089 6.59525 27.3614 6.69231 28.8151 6.83149C30.4224 6.98663 32.0303 7.13584 33.6388 7.3331C35.9198 7.60628 38.1897 7.92627 40.47 8.20538C42.0904 8.40386 43.7114 8.59642 45.3646 8.49847C46.3027 8.4401 47.2618 8.62379 48.1927 8.79256C49.5336 9.03394 50.885 9.07253 52.239 9.14136C53.8902 9.22311 55.5421 9.29894 57.1985 9.38723C57.6735 9.41263 58.1432 9.48547 58.6182 9.51087C58.7481 9.52438 58.8865 9.46081 59.0203 9.43874C58.9685 9.31341 58.9514 9.14372 58.8583 9.06807C58.3052 8.61476 57.7305 8.19518 57.1775 7.74187C55.7106 6.53386 54.2437 5.32585 52.784 4.1066C52.2211 3.63427 51.8989 2.98308 51.5571 2.34784C51.3492 1.9604 51.5341 1.63781 51.7218 1.34547C51.9021 1.06438 52.0235 0.669208 52.4453 0.635111C52.7346 0.611222 53.0239 0.587334 53.3159 0.593703C54.7643 0.618392 56.0541 1.16031 57.3235 1.77807C58.2302 2.22017 59.1204 2.70255 60.0415 3.12217C60.4279 3.30028 60.6627 3.55259 60.8241 3.92919C60.9383 4.19293 61.0275 4.52005 61.4388 4.203C61.6212 4.06605 61.8429 4.11309 62.0102 4.32839C62.2563 4.64183 62.5469 4.92992 62.7922 5.24929C62.9589 5.47051 63.1498 5.52635 63.422 5.54866C64.448 5.61337 65.2517 6.12273 65.8284 6.95631C66.199 7.4926 66.6963 7.85616 67.2599 8.10666C67.9146 8.39663 68.5004 8.76939 68.9623 9.29118C69.8119 10.2491 70.6155 11.2442 71.4126 12.2446C71.7531 12.6758 71.6567 13.4934 71.2651 13.9024C70.5625 14.639 69.8337 15.3428 69.1476 16.093C68.7802 16.4986 68.4883 16.978 68.1472 17.4163C68.0081 17.5937 67.8625 17.8305 67.6703 17.8944C66.9192 18.1522 66.4311 18.7311 65.8676 19.2362C65.6354 19.4459 65.3887 19.6782 65.1073 19.7928C64.1935 20.1716 63.2705 20.5253 62.3343 20.8358C62.0614 20.9274 61.7282 20.8087 61.4231 20.813C61.2854 20.8167 61.0794 20.8432 61.0217 20.9332C60.5146 21.738 59.7602 22.1333 58.8588 22.1835C57.4825 22.2623 56.1856 22.7031 54.8565 23.0027C53.3253 23.3472 51.7949 23.6858 50.267 24.0547C49.0888 24.34 47.9008 24.4443 46.6976 24.4151C46.1853 24.4038 45.8133 24.1493 45.6316 23.7406C45.2412 22.8424 45.1435 22.8622 45.7706 22.0519C45.8893 21.8963 46.0244 21.7544 46.1209 21.5846C46.4935 20.9157 47.1521 20.7922 47.8029 20.5781C48.9181 20.2143 50.011 19.7822 51.0986 19.3436C52.5241 18.7662 53.93 18.1508 55.345 17.5603C56.1092 17.2379 56.8866 16.959 57.6358 16.6111C58.3849 16.2632 59.1046 15.8583 59.9219 15.4335L59.9377 15.3992Z"
				fill="currentColor"
			/>
		</svg>
	);
};

const AnimatedArrowButton = ({
	href,
	text,
	onClick,
	label,
	labelEnglish,
}: {
	text: string;
	href: string;
	onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
	label: string;
	labelEnglish: string;
}) => {
	const { ref: scrollRef, inView: sectionIsVisible } = useInView({
		threshold: 1,
	});

	return (
		<div css={buttonContainer} ref={scrollRef}>
			<Adg4Button
				href={href}
				color="blue"
				size="large"
				onClick={onClick}
				dataEventProps={{
					'data-product-key': 'confluence',
					'data-event': 'clicked',
					'data-event-component': 'linkButton',
					'data-event-container': 'scrollPinnedSection',
					'data-label': label,
					'data-label-english': labelEnglish,
				}}
			>
				{text}
			</Adg4Button>
			<ArrowPencilPointRight rotate={180} right={-88} top="25%" animate={sectionIsVisible} />
			<ArrowPencilPointRight rotate={-140} right={-61} bottom={-49} animate={sectionIsVisible} />
			<ArrowPencilPointRight rotate={-121} right={0} bottom={-62} animate={sectionIsVisible} />
		</div>
	);
};

const ScrollerContent = ({ item }: { item: ScrollerItem }) => {
	const { ref } = useScrollTracking(`ScrollerSectionItem-${item.id}`);
	return (
		<div css={contentStyles} ref={ref}>
			<h3 css={headingStyles}>{item.title}</h3>
			<p css={textStyles}>{item.description}</p>
			{item.link && item.link.type === 'link' && (
				<Link
					text={item.link.text}
					aria-label={item.link.ariaLabelText}
					href={item.link.href}
					onClick={item.link.onClick}
					dataEventProps={{
						'data-product-key': 'confluence',
						'data-event': 'clicked',
						'data-event-component': 'linkButton',
						'data-event-container': 'scrollPinnedSection',
						'data-label': item.link.label,
						'data-label-english': item.link.labelEnglish,
					}}
				/>
			)}
			{item.link && item.link.type === 'button' && (
				<TryIntent
					intentType={'button'}
					containerId={'scrollPinnedSection'}
					productKey={'confluence'}
					edition={'free'}
				>
					<AnimatedArrowButton
						href={item.link.href}
						text={item.link.text}
						onClick={item.link.onClick}
						label={item.link.label}
						labelEnglish={item.link.labelEnglish}
					/>
				</TryIntent>
			)}
		</div>
	);
};

const ScrollerSection = () => {
	const intl = useIntl();
	const featuresLink = useLocalizedHref('/software/confluence/features');
	const signupLink = useLocalizedHref('/try/cloud/signup?bundle=confluence');
	const { openCrossFlow } = useCrossFlowFrontendTouchpoint({
		productKey: ProductKeys.CONFLUENCE,
		edition: Editions.FREE,
		redirectUrl: signupLink,
	});
	const { ref } = useScrollTracking('ScrollerSection', {
		observerOptions: {
			threshold: 0.1,
		},
	});
	const items: ScrollerArray<ScrollerItem> = [
		{
			id: '1-files',
			title: intl.formatMessage({
				id: 'ConfluenceSoftwarePage.v4.scroller.1.title',
				defaultMessage: 'From files to freedom',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePage.v4.scroller.1.description',
				defaultMessage:
					'Create dynamic Pages with all kinds of content, like Whiteboards, Databases, and videos. Pull in all your work to reduce context-switching.',
			}),
			feature: {
				src: 'https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/confluence-scroll-2.webp',
				alt: intl.formatMessage({
					id: 'ConfluenceSoftwarePage.v4.scroller.1.imgAlt',
					defaultMessage: 'image of a confluence page',
				}),
			},
			bgColor: wacToken('color.primary.lime'),
			lazyLoadImage: true,
			bgShape:
				'0% 29%, 42% 28%, 43% 5%, 100% 0%, 100% 95%, 60% 95%, 60% 100%, 2% 93%, 2% 93%, 2% 93%',
			link: {
				type: 'link',
				text: intl.formatMessage({
					id: 'ConfluenceSoftwarePage.v4.scroller.1.linkText',
					defaultMessage: 'See more features',
				}),
				label: 'see-more-features',
				labelEnglish: 'See more features',
				href: featuresLink,
				ariaLabelText: intl.formatMessage({
					id: 'ConfluenceSoftwarePage.v4.scroller.1.linkAriaLabel',
					defaultMessage: 'See more features of Confluence',
				}),
			},
		},
		{
			id: '2-work',
			title: intl.formatMessage({
				id: 'ConfluenceSoftwarePage.v4.scroller.2.title',
				defaultMessage: 'Your shortcut for accelerating work',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePage.v4.scroller.2.description',
				defaultMessage:
					'Eliminate writer’s block, automate repetitive tasks, and summarize lengthy docs with AI.',
			}),
			feature: {
				src: 'https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/confluence-scroll-1-updated.webp',
				alt: intl.formatMessage({
					id: 'ConfluenceSoftwarePage.v4.scroller.2.imgAlt',
					defaultMessage: 'image of Atlassian Intelligence on a confluence page',
				}),
			},
			bgColor: wacToken('color.primary.purple'),
			lazyLoadImage: true,
			bgShape: '6% 28%, 52% 23%, 53% 5%, 100% 0%, 100% 99%, 6% 96%, 6% 89%, 0% 88%, 0% 51%, 7% 50%',
			link:
				intl.locale === 'en'
					? {
							type: 'link',
							text: intl.formatMessage({
								id: 'ConfluenceSoftwarePage.v4.scroller.2.linkText',
								defaultMessage: 'Try it out',
							}),
							label: 'try-it-out',
							labelEnglish: 'Try it out',
							href: 'https://www.atlassian.com/confluence-sandbox-editor',
							ariaLabelText: intl.formatMessage({
								id: 'ConfluenceSoftwarePage.v4.scroller.2.linkAriaLabel',
								defaultMessage: 'Try out Confluence for free',
							}),
						}
					: undefined,
		},
		{
			id: '3-teams',
			title: intl.formatMessage({
				id: 'ConfluenceSoftwarePage.v4.scroller.3.title',
				defaultMessage: 'Connect the dots across your teams',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePage.v4.scroller.3.description',
				defaultMessage:
					"Build a source of truth for all teams. Stay caught up across your company with organized content and knowledge that's easily findable and always up-to-date.",
			}),
			feature: {
				src: 'https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/confluence-scroll-3.webp',
				alt: intl.formatMessage({
					id: 'ConfluenceSoftwarePage.v4.scroller.3.imgAlt',
					defaultMessage: 'image of a deliverable & outcomes document in confluence',
				}),
			},
			bgColor: wacToken('color.primary.saffron'),
			lazyLoadImage: true,
			bgShape:
				'0% 25%, 42% 20%, 42% 5%, 100% 0%, 100% 95%, 51% 100%, 50% 94%, 3% 95%, 3% 95%, 3% 95%',
			link: {
				type: 'button',
				text: intl.formatMessage({
					id: 'ConfluenceSoftwarePage.v4.scroller.3.linkText',
					defaultMessage: 'Get started free',
				}),
				label: 'get-started-free',
				labelEnglish: 'Get started free',
				href: signupLink,
				onClick: openCrossFlow,
				ariaLabelText: intl.formatMessage({
					id: 'ConfluenceSoftwarePage.v4.scroller.3.linkAriaLabel',
					defaultMessage: 'Get started with Confluence for free',
				}),
			},
		},
	];

	return (
		<UFOSegment name="confluence-pt-cfind-1234-scrollerSection">
			<section css={sectionStyle} ref={ref}>
				<Scroller
					items={items}
					renderText={(item) => <ScrollerContent item={item} />}
					renderGraphic={(item, activeIndex, mobileSpecific) => (
						<Adg4FramedImage
							feature={item.feature}
							accent={item.accent}
							bgShape={item.bgShape}
							bgColor={item.bgColor}
							lazyLoadImage={item.lazyLoadImage}
							activeIndexIsEven={!mobileSpecific ? activeIndex % 2 === 0 : undefined}
						/>
					)}
					layout="right"
					mobileGraphicLayout="above"
					backgroundColor="#fff"
					inScrollBackgroundColor="#fff"
					heading={
						<h2 css={scrollerHeading}>
							<FormattedMessage
								id="ConfluenceSoftwarePage.v4.scroller.heading"
								defaultMessage="Your team’s knowledge, all in one place"
							/>
						</h2>
					}
				/>
			</section>
		</UFOSegment>
	);
};

export default ScrollerSection;
